/**
 * SFX recipe layers (audio-direction §3, §7). A sound's recipe is a list of layers in `sfx.json`; each layer kind
 * has a Zod schema (its tuning) and a pure builder that turns (layer, seeded Rng) into Web Audio nodes on the
 * pre-render context. Same layer + same seed → the same graph (render hash), which the tests pin down.
 *
 * Layer kinds:
 * - `tone`     oscillators with a pitch sweep, optional drive, filter sweep and amplitude flutter (thuds, zaps, hums)
 * - `noise`    filtered seeded noise bursts (cracks, bodies, hiss, rumble, whooshes, puffs)
 * - `partials` inharmonic decaying sine modes (metallic ticks, clanks, hull ring)
 * - `clicks`   sparse click bursts through a resonator bank (mechanical rattle, crackle, debris)
 * - `fm`       two-operator FM with a sweeping index (energy buzz)
 * - `groan`    a wandering-pitch source through formant resonances (metal groan, creak)
 * - `stack`    detuned saw stacks with drive and an opening filter (the BRAAAM)
 * - `notes`    clean tonal sequences (ship-computer tones, lock beeps)
 * - `shepard`  octave-spaced rising voices under a bell curve (the endless riser)
 * Every layer has `at` (start, s), `gain`, `pan` (stereo renders only) and `vary` (per-variant random spread
 * of its frequencies and times).
 */
import { z } from 'zod'
import type { Rng } from '../../core/rng'
import { ahd, bipolar, clickSource, jitter, noiseSource, saturator, SILENT, sweep, wanderCurve } from './dsp'

const hz = z.number().positive().max(24000)
const sec = z.number().nonnegative().max(30)
const level = z.number().min(0).max(8)

const filterSchema = z.object({
  type: z.enum(['lowpass', 'highpass', 'bandpass', 'lowshelf', 'highshelf', 'peaking', 'notch']),
  hz,
  /** Sweep target (exponential) and its duration from the layer start. */
  hzEnd: hz.optional(),
  sweep: sec.default(0.1),
  q: z.number().min(0.0001).max(1000).default(0.707),
  db: z.number().min(-40).max(40).default(0),
})

const amSchema = z.object({
  /** Flutter rate (Hz) and depth (0..1 of the level). */
  hz: z.number().positive().max(400),
  depth: z.number().min(0).max(1),
  /** The flutter fades out over this many seconds (T60); omitted = constant. */
  decay: sec.optional(),
})

const common = {
  at: sec.default(0),
  gain: level,
  pan: z.number().min(-1).max(1).default(0),
  vary: z.number().min(0).max(0.5).default(0.04),
}

const envelope = {
  attack: sec.default(0.002),
  hold: sec.default(0),
  /** Seconds to −60 dB after the hold. */
  decay: sec,
}

export const toneLayerSchema = z.object({
  kind: z.literal('tone'),
  ...common,
  ...envelope,
  wave: z.enum(['sine', 'triangle', 'sawtooth', 'square']).default('sine'),
  hz,
  hzEnd: hz.optional(),
  /** Pitch sweep duration (exponential) from the layer start. */
  sweep: sec.default(0.05),
  voices: z.number().int().min(1).max(8).default(1),
  detuneCents: z.number().min(0).max(100).default(0),
  drive: z.number().min(0).max(60).default(0),
  filter: filterSchema.optional(),
  am: amSchema.optional(),
})

export const noiseLayerSchema = z.object({
  kind: z.literal('noise'),
  ...common,
  ...envelope,
  color: z.enum(['white', 'pink', 'brown']).default('white'),
  /** Decorrelated left/right noise in stereo renders. */
  wide: z.boolean().default(false),
  drive: z.number().min(0).max(60).default(0),
  filter: filterSchema.optional(),
  filter2: filterSchema.optional(),
  am: amSchema.optional(),
})

export const partialsLayerSchema = z.object({
  kind: z.literal('partials'),
  ...common,
  attack: sec.default(0.001),
  hz,
  modes: z.array(z.object({ ratio: z.number().positive().max(64), decay: sec, gain: level })).min(1).max(16),
})

const resonatorSchema = z.object({ type: z.enum(['bandpass', 'highpass', 'lowpass', 'peaking']).default('bandpass'), hz, q: z.number().min(0.0001).max(1000).default(4), gain: level.default(1) })

export const clicksLayerSchema = z.object({
  kind: z.literal('clicks'),
  ...common,
  count: z.number().int().min(1).max(400),
  span: sec,
  thinning: z.number().min(0.2).max(6).default(1),
  clickDecay: z.number().positive().max(0.2),
  fade: z.number().min(0).max(1).default(0.5),
  /** Stereo spread of the clicks (0 = at `pan`, 1 = full width). */
  spread: z.number().min(0).max(1).default(0),
  resonators: z.array(resonatorSchema).max(8).default([]),
  /** Level of the unfiltered clicks mixed with the resonators. */
  dry: level.default(0),
})

export const fmLayerSchema = z.object({
  kind: z.literal('fm'),
  ...common,
  ...envelope,
  hz,
  hzEnd: hz.optional(),
  sweep: sec.default(0.1),
  ratio: z.number().positive().max(32),
  /** Modulation index (deviation / carrier frequency) at the start and the end of `indexSweep`. */
  index: z.number().min(0).max(64),
  indexEnd: z.number().min(0).max(64).default(0),
  indexSweep: sec.default(0.1),
  filter: filterSchema.optional(),
})

export const groanLayerSchema = z.object({
  kind: z.literal('groan'),
  ...common,
  ...envelope,
  wave: z.enum(['sawtooth', 'square', 'triangle']).default('sawtooth'),
  hz,
  /** Pitch wander depth (fraction) and points per second of the random walk. */
  wander: z.number().min(0).max(2),
  wanderRate: z.number().positive().max(400),
  smooth: z.number().min(0).max(0.99).default(0.6),
  drive: z.number().min(0).max(60).default(0),
  formants: z.array(resonatorSchema).min(1).max(6),
  am: amSchema.optional(),
})

export const stackLayerSchema = z.object({
  kind: z.literal('stack'),
  ...common,
  ...envelope,
  hz,
  /** Semitones above `hz` of each stacked voice. */
  intervals: z.array(z.number().min(-36).max(48)).min(1).max(8),
  saws: z.number().int().min(1).max(5),
  detuneCents: z.number().min(0).max(60),
  /** Stereo spread of the voices. */
  spread: z.number().min(0).max(1).default(0.4),
  drive: z.number().min(0).max(60).default(0),
  /** The brass "blat": low-pass opening from `startHz` to `peakHz` at `peakAt`, then `midHz` at `midAt`, `endHz` at the end. */
  cutoff: z.object({ startHz: hz, peakHz: hz, peakAt: sec, midHz: hz, midAt: sec, endHz: hz, q: z.number().min(0.0001).max(30).default(1) }),
})

export const notesLayerSchema = z.object({
  kind: z.literal('notes'),
  ...common,
  wave: z.enum(['sine', 'triangle', 'square', 'sawtooth']).default('sine'),
  /** Extra sine partials (ratio, relative gain) on every note. */
  harmonics: z.array(z.object({ ratio: z.number().positive().max(16), gain: level })).max(6).default([]),
  attack: sec.default(0.004),
  release: sec.default(0.02),
  notes: z.array(z.object({ hz, at: sec, dur: z.number().positive().max(10), gain: level.default(1) })).min(1).max(64),
  filter: filterSchema.optional(),
})

export const shepardLayerSchema = z.object({
  kind: z.literal('shepard'),
  ...common,
  /** Lowest voice at the loop start, number of octave-spaced voices, rise per cycle (+1 up, −1 down). */
  hz,
  voices: z.number().int().min(2).max(12),
  direction: z.union([z.literal(1), z.literal(-1)]).default(1),
  /** Seconds for each voice to climb one octave (the loop length). */
  cycle: z.number().positive().max(60),
  /** Bell curve over the voice's octave position: centre (octaves above `hz`) and width (sigma, octaves). */
  center: z.number().min(0).max(12),
  width: z.number().positive().max(6),
  wave: z.enum(['sine', 'triangle']).default('sine'),
  filter: filterSchema.optional(),
})

export const layerSchema = z.discriminatedUnion('kind', [
  toneLayerSchema,
  noiseLayerSchema,
  partialsLayerSchema,
  clicksLayerSchema,
  fmLayerSchema,
  groanLayerSchema,
  stackLayerSchema,
  notesLayerSchema,
  shepardLayerSchema,
])

export type Layer = z.infer<typeof layerSchema>
export type LayerKind = Layer['kind']
type FilterSpec = z.infer<typeof filterSchema>
type AmSpec = z.infer<typeof amSchema>

/** Where a layer builds: the pre-render context, the node to feed, the render's channel count and its seeded Rng. */
export interface LayerTarget {
  readonly ac: BaseAudioContext
  readonly out: AudioNode
  readonly channels: 1 | 2
  readonly rng: Rng
  /** The render's total length (s): loops extend sustained layers to it. */
  readonly seconds: number
}

function filterNode(t: LayerTarget, f: FilterSpec, t0: number, vary: number): BiquadFilterNode {
  const node = t.ac.createBiquadFilter()
  node.type = f.type
  node.Q.value = f.q
  node.gain.value = f.db
  const from = jitter(t.rng, f.hz, vary)
  if (f.hzEnd === undefined) node.frequency.value = from
  else sweep(node.frequency, t0, from, (f.hzEnd * from) / f.hz, f.sweep)
  return node
}

/** `source → [drive] → [filter] → [filter2] → returns the last node` (not yet connected onward). */
function chain(t: LayerTarget, first: AudioNode, t0: number, vary: number, drive: number, filter?: FilterSpec, filter2?: FilterSpec): AudioNode {
  let last = first
  if (drive > 0) {
    const s = saturator(t.ac, drive)
    last.connect(s)
    last = s
  }
  if (filter !== undefined) {
    const f = filterNode(t, filter, t0, vary)
    last.connect(f)
    last = f
  }
  if (filter2 !== undefined) {
    const f = filterNode(t, filter2, t0, vary)
    last.connect(f)
    last = f
  }
  return last
}

/** Optional amplitude flutter: returns the node to connect onward from. */
function flutter(t: LayerTarget, input: AudioNode, am: AmSpec | undefined, t0: number, end: number): AudioNode {
  if (am === undefined || am.depth <= 0) return input
  const g = t.ac.createGain()
  g.gain.value = 1 - am.depth / 2
  const lfo = t.ac.createOscillator()
  lfo.frequency.value = jitter(t.rng, am.hz, 0.05)
  const depth = t.ac.createGain()
  depth.gain.value = am.depth / 2
  if (am.decay !== undefined) {
    depth.gain.setValueAtTime(am.depth / 2, t0)
    depth.gain.setTargetAtTime(0, t0, Math.max(am.decay, 1e-3) / Math.log(1000))
  }
  lfo.connect(depth)
  depth.connect(g.gain)
  lfo.start(t0)
  lfo.stop(end)
  input.connect(g)
  return g
}

/** Connects `node` to the target output, through a stereo panner when the render is stereo and `pan` ≠ 0. */
function toOut(t: LayerTarget, node: AudioNode, pan: number): void {
  if (t.channels === 2 && pan !== 0) {
    const p = t.ac.createStereoPanner()
    p.pan.value = pan
    node.connect(p)
    p.connect(t.out)
  } else node.connect(t.out)
}

function startOf(t: LayerTarget, at: number, vary: number): number {
  return at > 0 ? jitter(t.rng, at, vary * 0.5) : 0
}

/** Loops sustain to the end of the render: the hold stretches to fill it. */
function holdFor(t: LayerTarget, t0: number, attack: number, hold: number, sustain: boolean): number {
  return sustain ? Math.max(hold, t.seconds - t0 - attack) : hold
}

function buildTone(t: LayerTarget, L: z.infer<typeof toneLayerSchema>, sustain: boolean): void {
  const { ac, rng } = t
  const t0 = startOf(t, L.at, L.vary)
  const f0 = jitter(rng, L.hz, L.vary)
  const f1 = L.hzEnd === undefined ? f0 : (L.hzEnd * f0) / L.hz
  const sum = ac.createGain()
  sum.gain.value = 1 / Math.sqrt(L.voices)
  const hold = holdFor(t, t0, L.attack, L.hold, sustain)
  const env = ac.createGain()
  const end = ahd(env.gain, t0, L.attack, hold, L.decay, L.gain)
  const oscs: OscillatorNode[] = []
  for (let v = 0; v < L.voices; v++) {
    const o = ac.createOscillator()
    o.type = L.wave
    if (L.voices > 1) o.detune.value = ((v / (L.voices - 1)) * 2 - 1) * L.detuneCents + bipolar(rng) * L.detuneCents * 0.15
    sweep(o.frequency, t0, f0, f1, L.sweep)
    o.connect(sum)
    oscs.push(o)
  }
  const shaped = chain(t, sum, t0, L.vary, L.drive, L.filter)
  shaped.connect(env)
  toOut(t, flutter(t, env, L.am, t0, end), L.pan)
  for (const o of oscs) {
    o.start(t0)
    o.stop(end)
  }
}

function buildNoise(t: LayerTarget, L: z.infer<typeof noiseLayerSchema>, sustain: boolean): void {
  const t0 = startOf(t, L.at, L.vary)
  const hold = holdFor(t, t0, L.attack, L.hold, sustain)
  const env = t.ac.createGain()
  const end = ahd(env.gain, t0, L.attack, hold, L.decay, L.gain)
  const { src, offset } = noiseSource(t.ac, t.rng, end - t0 + 0.01, L.color, L.wide && t.channels === 2 ? 2 : 1)
  const shaped = chain(t, src, t0, L.vary, L.drive, L.filter, L.filter2)
  shaped.connect(env)
  toOut(t, flutter(t, env, L.am, t0, end), L.pan)
  src.start(t0, offset)
  src.stop(end)
}

function buildPartials(t: LayerTarget, L: z.infer<typeof partialsLayerSchema>): void {
  const t0 = startOf(t, L.at, L.vary)
  const base = jitter(t.rng, L.hz, L.vary)
  const sum = t.ac.createGain()
  sum.gain.value = L.gain
  for (const m of L.modes) {
    const o = t.ac.createOscillator()
    // Modes drift a little against each other per variant: the ring changes colour, not just pitch.
    o.frequency.value = base * m.ratio * (1 + bipolar(t.rng) * L.vary * 0.25)
    const env = t.ac.createGain()
    const end = ahd(env.gain, t0, L.attack, 0, jitter(t.rng, m.decay, L.vary), m.gain)
    o.connect(env)
    env.connect(sum)
    o.start(t0)
    o.stop(end)
  }
  toOut(t, sum, L.pan)
}

function buildClicks(t: LayerTarget, L: z.infer<typeof clicksLayerSchema>): void {
  const t0 = startOf(t, L.at, L.vary)
  const src = clickSource(
    t.ac,
    t.rng,
    { count: L.count, start: 0, span: jitter(t.rng, L.span, L.vary), thinning: L.thinning, clickDecay: L.clickDecay, fade: L.fade },
    t.channels,
    L.spread,
  )
  const sum = t.ac.createGain()
  sum.gain.value = L.gain
  if (L.dry > 0 || L.resonators.length === 0) {
    const dry = t.ac.createGain()
    dry.gain.value = L.resonators.length === 0 ? 1 : L.dry
    src.connect(dry)
    dry.connect(sum)
  }
  for (const r of L.resonators) {
    const f = t.ac.createBiquadFilter()
    f.type = r.type
    f.frequency.value = jitter(t.rng, r.hz, L.vary)
    f.Q.value = r.q
    const g = t.ac.createGain()
    g.gain.value = r.gain
    src.connect(f)
    f.connect(g)
    g.connect(sum)
  }
  toOut(t, sum, L.pan)
  src.start(t0)
}

function buildFm(t: LayerTarget, L: z.infer<typeof fmLayerSchema>): void {
  const { ac, rng } = t
  const t0 = startOf(t, L.at, L.vary)
  const f0 = jitter(rng, L.hz, L.vary)
  const f1 = L.hzEnd === undefined ? f0 : (L.hzEnd * f0) / L.hz
  const carrier = ac.createOscillator()
  const mod = ac.createOscillator()
  sweep(carrier.frequency, t0, f0, f1, L.sweep)
  sweep(mod.frequency, t0, f0 * L.ratio, f1 * L.ratio, L.sweep)
  const dev = ac.createGain()
  dev.gain.setValueAtTime(L.index * f0, t0)
  dev.gain.linearRampToValueAtTime(L.indexEnd * f1, t0 + Math.max(L.indexSweep, 1e-3))
  mod.connect(dev)
  dev.connect(carrier.frequency)
  const env = ac.createGain()
  const end = ahd(env.gain, t0, L.attack, L.hold, L.decay, L.gain)
  chain(t, carrier, t0, L.vary, 0, L.filter).connect(env)
  toOut(t, env, L.pan)
  carrier.start(t0)
  mod.start(t0)
  carrier.stop(end)
  mod.stop(end)
}

function buildGroan(t: LayerTarget, L: z.infer<typeof groanLayerSchema>, sustain: boolean): void {
  const { ac, rng } = t
  const t0 = startOf(t, L.at, L.vary)
  const hold = holdFor(t, t0, L.attack, L.hold, sustain)
  const env = ac.createGain()
  const end = ahd(env.gain, t0, L.attack, hold, L.decay, L.gain)
  const o = ac.createOscillator()
  o.type = L.wave
  const base = jitter(rng, L.hz, L.vary)
  const dur = Math.max(end - t0, 0.01)
  o.frequency.setValueCurveAtTime(wanderCurve(rng, Math.ceil(dur * L.wanderRate), base, L.wander, L.smooth), t0, dur)
  const driven = chain(t, o, t0, L.vary, L.drive)
  const bank = ac.createGain()
  for (const r of L.formants) {
    const f = ac.createBiquadFilter()
    f.type = r.type
    f.frequency.value = jitter(rng, r.hz, L.vary)
    f.Q.value = r.q
    const g = ac.createGain()
    g.gain.value = r.gain
    driven.connect(f)
    f.connect(g)
    g.connect(bank)
  }
  bank.connect(env)
  toOut(t, flutter(t, env, L.am, t0, end), L.pan)
  o.start(t0)
  o.stop(end)
}

function buildStack(t: LayerTarget, L: z.infer<typeof stackLayerSchema>): void {
  const { ac, rng } = t
  const t0 = startOf(t, L.at, L.vary)
  const root = jitter(rng, L.hz, L.vary * 0.2)
  const sum = ac.createGain()
  sum.gain.value = 1 / Math.sqrt(L.intervals.length * L.saws)
  const env = ac.createGain()
  const end = ahd(env.gain, t0, L.attack, L.hold, L.decay, L.gain)
  const oscs: OscillatorNode[] = []
  L.intervals.forEach((semis, i) => {
    const pan = L.intervals.length > 1 ? ((i / (L.intervals.length - 1)) * 2 - 1) * L.spread : 0
    const p = t.channels === 2 ? ac.createStereoPanner() : null
    if (p !== null) {
      p.pan.value = pan
      p.connect(sum)
    }
    for (let s = 0; s < L.saws; s++) {
      const o = ac.createOscillator()
      o.type = 'sawtooth'
      o.frequency.value = root * Math.pow(2, semis / 12)
      o.detune.value = (L.saws > 1 ? (s / (L.saws - 1)) * 2 - 1 : 0) * L.detuneCents + bipolar(rng) * 3
      o.connect(p ?? sum)
      oscs.push(o)
    }
  })
  const driven = chain(t, sum, t0, 0, L.drive)
  const lp = ac.createBiquadFilter()
  lp.type = 'lowpass'
  lp.Q.value = L.cutoff.q
  const c = L.cutoff
  lp.frequency.setValueAtTime(c.startHz, t0)
  lp.frequency.exponentialRampToValueAtTime(c.peakHz, t0 + Math.max(c.peakAt, 1e-3))
  lp.frequency.exponentialRampToValueAtTime(c.midHz, t0 + Math.max(c.midAt, c.peakAt + 1e-3))
  lp.frequency.exponentialRampToValueAtTime(Math.max(c.endHz, SILENT), Math.max(end, t0 + c.midAt + 1e-3))
  driven.connect(lp)
  lp.connect(env)
  toOut(t, env, L.pan)
  for (const o of oscs) {
    o.start(t0)
    o.stop(end)
  }
}

function buildNotes(t: LayerTarget, L: z.infer<typeof notesLayerSchema>): void {
  const { ac } = t
  const t0 = startOf(t, L.at, 0)
  const sum = ac.createGain()
  sum.gain.value = L.gain
  for (const n of L.notes) {
    const start = t0 + n.at
    const stop = start + n.dur
    const env = ac.createGain()
    env.gain.setValueAtTime(0, start)
    env.gain.linearRampToValueAtTime(n.gain, start + Math.min(L.attack, n.dur / 2))
    env.gain.setValueAtTime(n.gain, Math.max(start + Math.min(L.attack, n.dur / 2), stop - L.release))
    env.gain.linearRampToValueAtTime(0, stop)
    // Notes drift by a quarter of `vary` per variation (ticks); ship-computer tones set vary 0 to stay exact.
    const hz = jitter(t.rng, n.hz, L.vary * 0.25)
    const o = ac.createOscillator()
    o.type = L.wave
    o.frequency.value = hz
    o.connect(env)
    o.start(start)
    o.stop(stop + 0.01)
    for (const h of L.harmonics) {
      const ho = ac.createOscillator()
      ho.frequency.value = hz * h.ratio
      const hg = ac.createGain()
      hg.gain.value = h.gain
      ho.connect(hg)
      hg.connect(env)
      ho.start(start)
      ho.stop(stop + 0.01)
    }
    env.connect(sum)
  }
  toOut(t, chain(t, sum, t0, 0, 0, L.filter), L.pan)
}

function buildShepard(t: LayerTarget, L: z.infer<typeof shepardLayerSchema>): void {
  const { ac } = t
  const dur = t.seconds
  const sum = ac.createGain()
  sum.gain.value = L.gain / Math.sqrt(L.voices)
  const points = 96
  for (let v = 0; v < L.voices; v++) {
    const o = ac.createOscillator()
    o.type = L.wave
    const f0 = L.hz * Math.pow(2, v)
    const octaves = (L.direction * dur) / L.cycle
    sweep(o.frequency, 0, f0, f0 * Math.pow(2, octaves), dur)
    const g = ac.createGain()
    const curve = new Float32Array(points)
    for (let i = 0; i < points; i++) {
      // Unwrapped: a voice fades out past the top while the next loop pass brings voice 0 in at the bottom.
      // Rising with `center` = voices / 2, the top voice's exit level equals voice 0's entry level.
      const pos = v + (octaves * i) / (points - 1)
      const d = (pos - L.center) / L.width
      curve[i] = Math.exp(-0.5 * d * d)
    }
    g.gain.setValueCurveAtTime(curve, 0, dur)
    o.connect(g)
    g.connect(sum)
    o.start(0)
    o.stop(dur)
  }
  // Deterministic by design (no Rng): the loop point's phase mismatch is hidden by the render's crossfade.
  toOut(t, chain(t, sum, 0, 0, 0, L.filter), L.pan)
}

/** Builds one layer. `sustain` stretches holds to the render end (loops). */
export function buildLayer(t: LayerTarget, layer: Layer, sustain: boolean): void {
  switch (layer.kind) {
    case 'tone':
      return buildTone(t, layer, sustain)
    case 'noise':
      return buildNoise(t, layer, sustain)
    case 'partials':
      return buildPartials(t, layer)
    case 'clicks':
      return buildClicks(t, layer)
    case 'fm':
      return buildFm(t, layer)
    case 'groan':
      return buildGroan(t, layer, sustain)
    case 'stack':
      return buildStack(t, layer)
    case 'notes':
      return buildNotes(t, layer)
    case 'shepard':
      return buildShepard(t, layer)
  }
}
